import type { FC, ReactNode } from 'react';

import type { CmsAdapter, CmsPage, CmsSource } from '@eneco/cms-adapter';

import type { CmsAdapterSelector } from './cms-lane';

export interface CmsLaneSwitchProps {
  /** The page `resolveCmsLane` resolved server-side. Absent on every normal request. */
  cmsPage?: CmsPage | null;
  /** The provider the request forced, as `resolveCmsLane` returned it. */
  cmsForceProvider?: CmsSource | null;
  /** The host's provider selection, the same one its `getServerSideProps` used. */
  selectCmsAdapter: CmsAdapterSelector;
  /** Renders the CMS lane, typically a `CmsProvider` around the host's layout and registry. */
  render: (lane: { adapter: CmsAdapter; page: CmsPage }) => ReactNode;
  /** The host's own lane, rendered whenever the CMS lane does not apply. */
  children: ReactNode;
}

/**
 * Picks the lane a catch-all page renders: `render` when the request forced a provider that resolved
 * a page, and `children` otherwise. The adapter is selected again on the client, since an adapter
 * cannot travel in page props; where the host's gate is closed that gives `undefined` and the host's
 * own lane renders.
 */
export const CmsLaneSwitch: FC<CmsLaneSwitchProps> = ({
  cmsPage,
  cmsForceProvider,
  selectCmsAdapter,
  render,
  children,
}) => {
  const adapter = cmsForceProvider ? selectCmsAdapter(cmsForceProvider) : undefined;

  if (!cmsPage || !adapter) return children;

  return render({ adapter, page: cmsPage });
};
